/**
 * ThreadCard — composite card for email threads in Queue/Kanban/Calendar.
 *
 * Shows thread subject, participant avatars, message count, latest snippet,
 * domain badge, and priority indicator. Visually distinguishes threads
 * (multi-email) from single tasks/emails.
 *
 * Props:
 *   thread  — { threadId, subject, emails[], highestPriority, latestEmail }
 *   variant — 'queue' | 'kanban' | 'calendar' (controls layout density)
 *   draggable, onDragStart — for kanban drag support
 *   actions — optional ActionPill content (queue view)
 *   style   — pass-through styles (kanban opacity, etc.)
 */
import React, { useState } from 'react';
import { MessageSquare, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { cn, ICON_PX } from '../../../ui';
import { DomainBadge } from './DomainBadge';
import { TimeAgo } from './TimeAgo';
// UrgencyIndicator available for Phase 2 thread-level urgency display

const PRIORITY_BORDER = {
  critical: 'var(--red-critical)',
  high:     'var(--red-critical)',
  medium:   'var(--yellow)',
  low:      'var(--green)',
};

function ThreadCard({ thread, variant = 'queue', draggable, onDragStart, actions, style, className }) {
  const [expanded, setExpanded] = useState(false);
  const isThread = thread.emails.length > 1;
  const latest = thread.latestEmail;

  // Unique sender names
  const senders = [...new Set(thread.emails.map((e) => e.from.name))];
  const sendersLabel = senders.length <= 2
    ? senders.join(', ')
    : `${senders[0]}, ${senders[1]} +${senders.length - 2}`;

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={cn(
        'rounded-lg border border-nn-border bg-nn-white overflow-hidden transition-shadow hover:shadow-md',
        className,
      )}
      style={{
        borderLeft: `3px solid ${PRIORITY_BORDER[thread.highestPriority] ?? 'var(--text-muted)'}`,
        ...style,
      }}
    >
      {/* Header — always visible */}
      <div className={cn('px-3 py-2', variant === 'kanban' && 'px-2.5 py-1.5')}>
        {/* Row 1: subject + thread indicator */}
        <div className="flex items-center gap-1.5 min-w-0">
          {isThread && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              className="shrink-0 text-text-muted hover:text-text-dark transition-colors p-0.5"
            >
              {expanded
                ? <ChevronDown size={ICON_PX.sm} />
                : <ChevronRight size={ICON_PX.sm} />
              }
            </button>
          )}
          {isThread ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              className={cn(
                'text-[12px] font-semibold text-text-dark truncate min-w-0 flex-1 text-left hover:text-aqua transition-colors',
                variant === 'kanban' && 'text-[11px]',
              )}
            >
              {thread.subject}
            </button>
          ) : (
            <span className={cn(
              'text-[12px] font-semibold text-text-dark truncate min-w-0 flex-1',
              variant === 'kanban' && 'text-[11px]',
            )}>
              {thread.subject}
            </span>
          )}
          {isThread && (
            <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold text-aqua bg-aqua/10 px-1.5 py-0.5 rounded-full">
              <MessageSquare size={9} />
              {thread.emails.length}
            </span>
          )}
          {/* Actions inline — right side (queue view) */}
          {actions && <div className="shrink-0 ml-auto">{actions}</div>}
        </div>

        {/* Row 2: domain + senders + time */}
        <div className="flex items-center gap-1.5 mt-1 min-w-0">
          <DomainBadge domain={latest.primaryDomain} />
          {isThread && (
            <span className="inline-flex items-center gap-0.5 text-text-muted shrink-0">
              <Users size={9} />
            </span>
          )}
          <span className="text-[10px] text-text-dark-secondary truncate min-w-0">
            {sendersLabel}
          </span>
          <TimeAgo date={latest.receivedAt} className="ml-auto !text-[10px]" />
        </div>

        {/* Row 3: latest preview (queue/calendar only) */}
        {variant !== 'kanban' && (
          <div className="text-[10px] text-text-muted truncate min-w-0 mt-1">
            {latest.preview}
          </div>
        )}

        {/* Scheduled date (if present) */}
        {latest.scheduledDate && variant === 'kanban' && (
          <div className="text-[9px] text-text-muted mt-1">
            Scheduled: {latest.scheduledDate}
          </div>
        )}
      </div>

      {/* Expanded thread messages (click chevron to toggle) */}
      {isThread && expanded && (
        <div className="border-t border-nn-border px-3 py-2 flex flex-col gap-1.5 bg-surface/30">
          {thread.emails.map((email) => (
            <div key={email.id} className="flex items-start gap-2 min-w-0">
              <span
                className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                style={{ background: email.isRead ? 'var(--surface)' : 'var(--aqua)' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-[10px] font-medium text-text-dark truncate min-w-0">
                    {email.from.name}
                  </span>
                  <TimeAgo date={email.receivedAt} className="ml-auto !text-[9px]" />
                </div>
                <div className="text-[10px] text-text-muted truncate min-w-0">
                  {email.preview}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Helper: group an array of emails into thread objects for ThreadCard.
 *
 * Returns: [{ threadId, subject, emails[], highestPriority, latestEmail }]
 */
function groupEmailsIntoThreads(emails) {
  const threadMap = {};

  emails.forEach((email) => {
    const key = email.threadId || email.id;
    if (!threadMap[key]) threadMap[key] = [];
    threadMap[key].push(email);
  });

  return Object.entries(threadMap).map(([threadId, threadEmails]) => {
    const sorted = [...threadEmails].sort(
      (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
    );
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const highestPriority = sorted.reduce(
      (best, e) => (priorityOrder[e.priority] ?? 4) < (priorityOrder[best] ?? 4) ? e.priority : best,
      sorted[0].priority,
    );

    return {
      threadId,
      subject: sorted[0].subject.replace(/^(RE|FWD|Fwd|Fw|Re):\s*/g, ''),
      emails: sorted,
      highestPriority,
      latestEmail: sorted[0],
    };
  });
}

export { ThreadCard, groupEmailsIntoThreads };
